import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import type {
    MeasurementStat,
    MeasurementStatDimension,
    MeasurementStatQuestion,
} from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { QuestionScoreQuestion } from "@gss/types/statScore";
import { Chart } from "../charts/Chart";
import { DAO } from "../DAO";
import type { FilterSerie, QuestionsDataList } from "../filters/filters.types";
import { useTranslation } from "../util/Translation";
import type { GetTranslation } from "../util/Translation";

interface Props {
    stat: MeasurementStat;
    questions: MeasurementStatQuestion[];
    subtitle: string;
    filterSeries: FilterSerie[];
}

export function QuestionOverviewTotal({
    stat,
    questions,
    subtitle,
    filterSeries,
}: Props): JSX.Element {
    const [data, setData] = useState<QuestionsDataList[]>();
    const { translation, getTranslation } = useTranslation();

    useEffect(() => {
        void (async () => {
            const data = await Promise.all(
                filterSeries.map((fs) =>
                    getData(stat.securityContext, questions, fs),
                ),
            );
            setData(data);
        })();
    }, [filterSeries]);

    const title = translation.stats.questionOverviewTotal;
    return (
        <Chart
            variant="bar"
            className="chart-question-overview"
            height={700 + 200 * filterSeries.length}
            title={title}
            subtitle={subtitle}
            yTitle={translation.stats.meanValueYAxis}
            filename={`${stat.measurement} ${title}`}
            categories={getOverviewCategories(getTranslation, questions)}
            series={data}
            callback={(s) =>
                getOverviewData(getTranslation, questions, s.questions)
            }
            xFormatter={(e) => {
                if (typeof e.value !== "string") {
                    return "";
                }
                return `<span title="${e.value}">${e.value}</span>`;
            }}
        />
    );
}

export function getOverviewCategories(
    getTranslation: GetTranslation,
    questions: MeasurementStatQuestion[],
): string[] {
    return questions.map((q) => getOverviewText(getTranslation, q));
}

export function getOverviewData(
    getTranslation: GetTranslation,
    questions: MeasurementStatQuestion[],
    cardQuestions: (QuestionScoreQuestion | null)[],
): { data: (number | null)[]; confidenceIntervals: Record<string, number> } {
    const data: (number | null)[] = [];
    const confidenceIntervals: Record<string, number> = {};
    for (const q of questions) {
        const cq = cardQuestions.find((cq) => cq?.id === q.id);
        if (cq?.status === "ok") {
            data.push(cq.score.value);
            const text = getOverviewText(getTranslation, q);
            confidenceIntervals[text] = cq.score.confidenceInterval;
        } else {
            data.push(null);
        }
    }
    return { data, confidenceIntervals };
}

export function filterOverviewQuestions(
    questions: MeasurementStatQuestion[],
    dimensions: MeasurementStatDimension[],
): MeasurementStatQuestion[] {
    const map = new Map<string, number>();
    for (const [i, dim] of dimensions.entries()) {
        map.set(dim.name, i);
    }
    return questions
        .filter((q) => map.has(q.dimension))
        .toSorted((a, b) => {
            const left = map.get(a.dimension) ?? 0;
            const right = map.get(b.dimension) ?? 0;
            return left - right;
        });
}

function getOverviewText(
    getTranslation: GetTranslation,
    question: MeasurementStatQuestion,
): string {
    return getTranslation(question.texts) ?? question.id;
}

function getData(
    audience: SecurityContext,
    questions: MeasurementStatQuestion[],
    filterSerie: FilterSerie,
): Promise<QuestionsDataList> {
    return new Promise((resolve, reject) => {
        DAO.stats
            .getQuestionScore(
                audience,
                filterSerie.measurement,
                filterSerie.orgStructure,
                filterSerie.periods,
                filterSerie.filters,
                filterSerie.orgIds,
            )
            .then((questionScore) => {
                const result = questions.map(
                    (q) =>
                        questionScore.questions.find((cq) => cq.id === q.id) ??
                        null,
                );
                resolve({ filterSerie, questions: result });
            })
            .catch(reject);
    });
}
